import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { useAuth } from '@/contexts/AuthContext';
import { useDisparos, DisparosProvider, DisparoLote } from '@/contexts/DisparosContext';
import RoleBasedNavigation from '@/components/RoleBasedNavigation';
import { useConfirmation } from '@/hooks/useConfirmation';
import { formatDateBR, formatCarimboBR } from '@/utils/dateUtils';
import {
  Search,
  Filter,
  BarChart,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  X,
  DollarSign,
  Calendar,
  FileText,
  Users,
  TrendingUp,
  TrendingDown,
  Send,
  AlertTriangle,
  Trash2,
  HelpCircle,
  Database,
  UserCheck,
  Pause,
  Upload,
  RefreshCw,
  Download,
  ChevronsUpDown,
  Gift,
  CreditCard,
  FileSearch,
  Mail
} from 'lucide-react';
import api from '@/lib/api';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import ImportBoletosModal from '@/components/ImportBoletosModal';
import ImportDistribuicaoModal from '@/components/ImportDistribuicaoModal';
import ConciliarBoletosModal from '@/components/ConciliarBoletosModal';
import { ClientSelectModal } from '@/components/ClientSelectModal';
import DailyProgressVisualization from '@/components/DailyProgressVisualization';
import HistoricoLotesModal from '@/components/HistoricoLotesModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { EmailLotesPanel } from '@/components/email/EmailLotesPanel';

// Usar a interface do contexto que já inclui todos os campos necessários
// interface DisparoLote já está definida em DisparosContext

interface WhatsappConnectionsResponse {
  connections: Array<{
    id: number;
    name: string;
    connection_type: string;
    is_active: boolean;
  }>;
}

interface DistribuicaoStatusResponse {
  success: boolean;
  hasDistribution: boolean;
  total: number;
}

interface DisparoBoleto {
  id: number;
  lote_id: number;
  nome: string;
  placa: string;
  modelo: string;
  valor: number;
  vencimento: string;
  contato: string;
  status: string;
  motivo_rejeicao?: string;
  data_envio?: string;
  hora_envio?: string;
  created_at: string;
  // Entrega real reportada pela Meta (via relay cleoia)
  delivery_status?: 'sent' | 'delivered' | 'read' | 'failed' | null;
  delivery_error_code?: number | null;
  delivery_error_title?: string | null;
  // Tradução PT + categoria do erro da Cloud API (vinda do backend)
  delivery_error_pt?: string | null;
  delivery_error_categoria?: string | null;
  delivery_error_categoria_label?: string | null;
  // Fase 3 (metawebhook): carimbos do ciclo de vida da entrega (ISO 8601 ou null)
  sent_at?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  failed_at?: string | null;
}

// Entrega real (Meta). "Aceito Meta" = aceito sem confirmação; "Entregue"/"Lido"
// = chegou/abriu; "Falhou" = não entregue (motivo no tooltip).
const DELIVERY_BADGE: Record<string, { label: string; className: string }> = {
  delivered: { label: 'Entregue', className: 'bg-emerald-100 text-emerald-700' },
  read: { label: 'Lido', className: 'bg-sky-100 text-sky-700' },
  sent: { label: 'Aceito Meta', className: 'bg-gray-100 text-gray-600' },
  failed: { label: 'Falhou', className: 'bg-red-100 text-red-700' },
};

// Motivo da falha em PT (com código e categoria) p/ tooltip/legenda.
const deliveryFailTooltip = (d: {
  delivery_error_pt?: string | null;
  delivery_error_title?: string | null;
  delivery_error_code?: number | null;
  delivery_error_categoria_label?: string | null;
}): string => {
  const base = d.delivery_error_pt || d.delivery_error_title || 'Falha na entrega';
  const code = d.delivery_error_code ? ` (cód ${d.delivery_error_code})` : '';
  const cat = d.delivery_error_categoria_label ? ` · ${d.delivery_error_categoria_label}` : '';
  const meta = d.delivery_error_title && d.delivery_error_title !== base ? ` · Meta: ${d.delivery_error_title}` : '';
  return `${base}${code}${cat}${meta}`;
};

const DisparosContent = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { lotes, isLoading: isLoadingLotes, refreshLotes, clienteId, statusFilter, setClienteId, setStatusFilter } = useDisparos();
  const { confirm, ConfirmationDialog } = useConfirmation();
  
  const [clientes, setClientes] = useState<{ id: number; legal_name: string; company_tax_id?: string; trade_name?: string }[]>([]);
  const [disparoTab, setDisparoTab] = useState<'whatsapp' | 'email'>('whatsapp');
  const [showClientModal, setShowClientModal] = useState(false);
  const [clienteNome, setClienteNome] = useState<string>('');
  const [isLoadingClientes, setIsLoadingClientes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLote, setSelectedLote] = useState<DisparoLote | null>(null);
  const [loteDisparos, setLoteDisparos] = useState<DisparoBoleto[]>([]);
  const [disparosLoading, setDisparosLoading] = useState(false);
  const [disparoSearchTerm, setDisparoSearchTerm] = useState('');
  const [disparoStatusFilter, setDisparoStatusFilter] = useState('all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'xlsx'>('pdf');
  const [exportStatusFilter, setExportStatusFilter] = useState('all');
  const [hasImportIntegration, setHasImportIntegration] = useState(false);
  const [clientSystemType, setClientSystemType] = useState<string>('');
  const [loadingIntegration, setLoadingIntegration] = useState(false);
  const [showDistribuicaoModal, setShowDistribuicaoModal] = useState(false);
  const [selectedLoteForDistribuicao, setSelectedLoteForDistribuicao] = useState<number | null>(null);
  const [showConciliacaoModal, setShowConciliacaoModal] = useState(false);
  const [selectedLoteForConciliacao, setSelectedLoteForConciliacao] = useState<number | null>(null);
  const [distribuicaoStatus, setDistribuicaoStatus] = useState<{ [key: number]: { hasDistribution: boolean; total: number } }>({});
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  
  // Estados para seleção de conexão (admin)
  const [adminConnections, setAdminConnections] = useState<any[]>([]);
  const [selectedAdminConnectionId, setSelectedAdminConnectionId] = useState<number | null>(null);
  const [loadingAdminConnections, setLoadingAdminConnections] = useState(false);
  const [isMarcandoFalhasComoPendente, setIsMarcandoFalhasComoPendente] = useState(false);
  const [isMarcandoBaixadosComoPendente, setIsMarcandoBaixadosComoPendente] = useState(false);
  const [enviarDialog, setEnviarDialog] = useState<{ open: boolean; loteId: number | null; emailEnabled: boolean }>({ open: false, loteId: null, emailEnabled: false });
  const [enviarEmailJunto, setEnviarEmailJunto] = useState(false);
  const [clienteEmailCanalEnabled, setClienteEmailCanalEnabled] = useState(false);

  // Opções de status para o filtro
  const statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'FILA', label: 'Fila' },
    { value: 'AGUARDANDO_CONFIRMACAO', label: 'Aguardando Confirmação' },
    { value: 'ENVIADO', label: 'Enviado' },
    { value: 'FALHA', label: 'Falha' },
    { value: 'CONTATO_INVALIDO', label: 'Contato Inválido' },
    { value: 'BAIXADO', label: 'Baixado' }
  ];

  // Buscar clientes se for super admin
  useEffect(() => {
    if (user?.role === 'admin') {
      setIsLoadingClientes(true);
      api.get('/clients').then((res: any) => {
        setClientes(res.clients || []);
      }).finally(() => setIsLoadingClientes(false));
    }
  }, [user]);

  // Carregar conexões quando clienteId muda (admin)
  useEffect(() => {
    if (user?.role === 'admin' && clienteId) {
      loadAdminConnections(clienteId);
    }
  }, [user?.role, clienteId]);

  const loadAdminConnections = async (targetClientId: number) => {
    try {
      setLoadingAdminConnections(true);
      console.log('🔍 [Disparos-Admin] Carregando conexões para cliente:', targetClientId);
      
      const response = await api.get(`/whatsapp/client/${targetClientId}`) as WhatsappConnectionsResponse;
      const allConnections = response?.connections || [];
      
      // Filtrar apenas conexões do tipo "oficial"
      const connections = allConnections.filter((conn: any) => 
        conn.connection_type === 'oficial'
      );
      
      console.log('📱 [Disparos-Admin] Total conexões:', allConnections.length);
      console.log('📱 [Disparos-Admin] Conexões oficiais:', connections.length);
      console.log('📱 [Disparos-Admin] Conexões filtradas:', connections);
      
      setAdminConnections(connections);
      
      // Selecionar primeira conexão automaticamente
      if (connections.length > 0) {
        const firstConnection = connections[0];
        setSelectedAdminConnectionId(firstConnection.id);
        console.log('🎯 [Disparos-Admin] Selecionando primeira conexão:', firstConnection.name);
      } else {
        setSelectedAdminConnectionId(null);
      }
      
    } catch (error) {
      console.error('❌ [Disparos-Admin] Erro ao carregar conexões:', error);
      setAdminConnections([]);
      setSelectedAdminConnectionId(null);
    } finally {
      setLoadingAdminConnections(false);
    }
  };

  // Carregar lotes quando cliente selecionado mudar
  useEffect(() => {
    console.log('🔄 [Disparos] useEffect executado');
    console.log('🔍 [Disparos] Estado atual:', { 
      authLoading, 
      user: user ? { id: user.id, role: user.role, client_id: user.client_id } : 'null',
      clienteId, 
      statusFilter 
    });
    
    // Aguardar a autenticação ser verificada
    if (authLoading) {
      console.log('⏳ [Disparos] Aguardando autenticação...');
      return;
    }
    
    // Para admins: só carregar se clienteId estiver definido
    // Para clientes: sempre carregar (usa user.client_id)
    if (user?.role === 'admin' && !clienteId) {
      console.log('⏳ [Disparos] Admin sem cliente selecionado');
      return;
    }
    if (user?.role === 'cliente' && !user.client_id) {
      console.log('⏳ [Disparos] Cliente sem client_id');
      return;
    }
    
    console.log('🚀 [Disparos] Iniciando carregamento de lotes...');
    handleLoadLotes();
  }, [clienteId, statusFilter, user, authLoading]);

  // CORREÇÃO: Removidos useEffects duplicados que causavam conflitos
  // A lógica de carregamento agora é gerenciada pelo DisparosContext

  // CORREÇÃO: Removido event listener de focus que causava recarregamentos desnecessários
  // O recarregamento automático estava causando zeramento dos dados quando a página perdia/ganhava foco

  // Monitorar mudanças no estado para debug
  useEffect(() => {
    console.log('🔄 [Disparos] Estado atualizado:', {
      user: user ? { id: user.id, role: user.role, client_id: user.client_id } : null,
      authLoading,
      lotesCount: lotes.length,
      clienteId,
      statusFilter
    });
  }, [user, authLoading, lotes.length, clienteId, statusFilter]);

  // Verificar integração de importação quando cliente mudar
  useEffect(() => {
    if (user?.role === 'admin' && clienteId) {
      checkImportIntegration(clienteId);
    } else if (user?.role === 'cliente' && user.client_id) {
      // Para clientes, verificar integração do próprio cliente
      checkImportIntegration(user.client_id);
    } else {
      // Limpar estados quando nenhum cliente selecionado
      setHasImportIntegration(false);
      setClientSystemType('');
      setClienteEmailCanalEnabled(false);
      setLoadingIntegration(false);
    }
  }, [clienteId, user]);

  // Função para verificar integração de importação (Hinova ou South)
  const checkImportIntegration = async (clientId: number) => {
    setLoadingIntegration(true);
    try {
      console.log(`🔍 [Disparos] Verificando integração para cliente ${clientId}`);
      
      // Buscar dados do cliente diretamente (api.ts já adiciona o prefixo /api)
      const clientResponse = await api.get(`/clients/${clientId}`);
      console.log(`📊 [Disparos] Resposta do cliente:`, clientResponse);
      
      // O endpoint /clients/:id retorna diretamente o objeto cliente
      if (clientResponse && (clientResponse as any).id) {
        const systemType = (clientResponse as any).system_management;
        console.log(`🔧 [Disparos] Sistema detectado: "${systemType}"`);
        setClientSystemType(systemType || '');
        setClienteEmailCanalEnabled(!!(clientResponse as any).email_canal_enabled);
        
        // Verificar se tem integração ativa para Hinova ou South
        if (systemType === 'hinova') {
          console.log(`🏢 [Disparos] Verificando integração Hinova...`);
          try {
            const response = await api.get(`/integrations/client/${clientId}`);
            console.log(`📋 [Disparos] Resposta integração Hinova:`, response);
            console.log(`📋 [Disparos] Integration object:`, (response as any).integration);
            console.log(`📋 [Disparos] System type:`, (response as any).integration?.system_type);
            console.log(`📋 [Disparos] Is active:`, (response as any).integration?.is_active);
            
            const hasHinovaIntegration = (response as any).integration?.system_type === 'hinova' && (response as any).integration?.is_active;
            console.log(`✅ [Disparos] Hinova integração ativa: ${hasHinovaIntegration}`);
            setHasImportIntegration(hasHinovaIntegration);
          } catch (hinovaError) {
            console.warn(`⚠️ [Disparos] Erro ao verificar integração Hinova:`, hinovaError);
            console.error(`⚠️ [Disparos] Detalhes do erro:`, hinovaError.response?.data);
            setHasImportIntegration(false);
          }
        } else if (systemType === 'south') {
          console.log(`🌟 [Disparos] Verificando integração South...`);
          // Para South, verificar se tem token e api_base_url configurados
          const hasToken = !!(clientResponse as any).system_token;
          const hasApiUrl = !!(clientResponse as any).api_base_url;
          console.log(`🔑 [Disparos] South - Token: ${hasToken}, API URL: ${hasApiUrl}`);
          console.log(`🔑 [Disparos] South - Token value: "${(clientResponse as any).system_token?.substring(0, 10)}..."`);
          console.log(`🔑 [Disparos] South - API URL value: "${(clientResponse as any).api_base_url}"`);
          
          const hasSouthIntegration = hasToken && hasApiUrl;
          console.log(`✅ [Disparos] South integração ativa: ${hasSouthIntegration}`);
          setHasImportIntegration(hasSouthIntegration);
        } else {
          // Para outros sistemas ou sem sistema definido, sempre mostrar o botão
          console.log(`📋 [Disparos] Sistema "${systemType}" - Mostrando botão de importação`);
          setHasImportIntegration(true);
        }
      } else {
        console.warn(`⚠️ [Disparos] Resposta inválida do cliente:`, clientResponse);
        setHasImportIntegration(false);
        setClientSystemType('');
      }
    } catch (error) {
      console.error('❌ [Disparos] Erro ao verificar integração:', error);
      setHasImportIntegration(false);
      setClientSystemType('');
    } finally {
      setLoadingIntegration(false);
    }
  };

  const handleLoadLotes = async () => {
    try {
      console.log('🔄 [Disparos] handleLoadLotes chamado para cliente:', clienteId);
      // Limpar dados antigos antes de carregar novos
      await refreshLotes();
      if (user?.role === 'admin' && clienteId) {
        const cliente = clientes.find(c => c.id === clienteId);
        setClienteNome(cliente?.legal_name || '');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar lotes:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar lotes de disparos",
        variant: "destructive"
      });
    }
  };

  const loadLoteDisparos = async (loteId: number) => {
    try {
      setDisparosLoading(true);
      console.log(`🔍 [Disparos] Carregando TODOS os disparos do lote ${loteId}`);
      
      // Solicitar todos os disparos (limit=0) para carregar o lote completo
      const response = await api.get(`/disparos/lotes/${loteId}?limit=0`);
      console.log(`📊 [Disparos] Resposta completa do backend:`, response);
      console.log(`🔍 [Disparos] Disparos recebidos:`, (response as any).disparos);
      console.log(`🔍 [Disparos] Primeiros 3 disparos com data_envio/hora_envio:`, 
        (response as any).disparos?.slice(0, 3).map((d: any) => ({
          id: d.id,
          status: d.status,
          data_envio: d.data_envio,
          hora_envio: d.hora_envio,
          data_envio_type: typeof d.data_envio,
          hora_envio_type: typeof d.hora_envio
        }))
      );
      
      setLoteDisparos((response as any).disparos || []);
      
      // CORREÇÃO: Atualizar selectedLote com informações completas do backend
      if ((response as any).lote && (response as any).estatisticas) {
        const loteInfo = (response as any).lote;
        const estatisticas = (response as any).estatisticas;
        console.log('📊 [Disparos] Informações do lote recebidas do backend:', loteInfo);
        console.log('📊 [Disparos] Estatísticas recebidas do backend:', estatisticas);
        
        const loteAtualizado = {
          ...selectedLote,
          id: loteInfo.id,
          nome: loteInfo.nome,
          tipo_envio: loteInfo.tipo_envio,
          status: loteInfo.status,
          data_vencimento: loteInfo.vencimento,
          valor_total: loteInfo.valor_total,
          quantidade_total: estatisticas.total,
          quantidade_enviados: estatisticas.enviados,
          quantidade_pendentes: estatisticas.pendentes,
          quantidade_fila: estatisticas.fila,
          quantidade_falhas: estatisticas.falhas,
          quantidade_invalidos: estatisticas.invalidos,
          quantidade_baixados: estatisticas.baixados,
          quantidade_excluidos: estatisticas.excluidos
        };
        
        console.log('📊 [Disparos] selectedLote após atualização:', loteAtualizado);
        setSelectedLote(loteAtualizado);
      } else {
        console.log('❌ [Disparos] Informações do lote não encontradas na resposta do backend');
      }
    } catch (error) {
      console.error('Erro ao carregar disparos do lote:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar disparos do lote",
        variant: "destructive"
      });
    } finally {
      setDisparosLoading(false);
    }
  };

  const handleViewDetails = (lote: DisparoLote) => {
    setSelectedLote(lote);
    setShowDetailsModal(true);
    setDisparoSearchTerm('');
    setDisparoStatusFilter('all');
    loadLoteDisparos(lote.id);
  };

  // Função para filtrar disparos para exportação
  const getFilteredDisparosForExport = () => {
    return loteDisparos.filter(disparo => {
      return exportStatusFilter === 'all' || disparo.status === exportStatusFilter;
    });
  };

  // Função para exportar em PDF
  const exportToPDF = () => {
    const filteredDisparos = getFilteredDisparosForExport();
    const isAniversario = selectedLote?.tipo_envio === 'ANIVERSARIO';
    
    const doc = new jsPDF('l', 'mm', 'a4');
    
    // Título
    doc.setFontSize(16);
    doc.text(`Relatório do Lote ${selectedLote?.id}`, 14, 22);
    
    // Informações do lote
    doc.setFontSize(10);
    doc.text(`Tipo de Envio: ${selectedLote?.tipo_envio}`, 14, 30);
    doc.text(`Status: ${selectedLote?.status}`, 14, 35);
    doc.text(`Total de Disparos: ${selectedLote?.quantidade_total}`, 14, 40);
    doc.text(`Filtro Aplicado: ${exportStatusFilter === 'all' ? 'Todos os Status' : exportStatusFilter}`, 14, 45);
    doc.text(`Registros Exportados: ${filteredDisparos.length}`, 14, 50);
    
    // Preparar dados da tabela - colunas condicionais por tipo
    const tableData = filteredDisparos.map(disparo => {
      const statusDisplay = (isAniversario && disparo.status === 'BAIXADO') ? 'Enviado' : disparo.status;
      const dataHoraEnvio = (disparo.data_envio && disparo.hora_envio && disparo.data_envio !== '' && disparo.hora_envio !== '') 
        ? `${disparo.data_envio} ${disparo.hora_envio}` : '-';
      if (isAniversario) {
        return [disparo.nome, disparo.vencimento || '-', disparo.contato, statusDisplay, dataHoraEnvio];
      }
      return [
        disparo.nome,
        disparo.placa || '-',
        disparo.modelo,
        `R$ ${(disparo.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        disparo.vencimento || '-',
        disparo.contato,
        statusDisplay,
        dataHoraEnvio
      ];
    });
    
    // Cabeçalhos da tabela - condicionais por tipo
    const headers = isAniversario 
      ? ['Nome', 'Data de Aniversário', 'Contato', 'Status', 'Data/Hora Envio']
      : ['Nome', 'Placa', 'Modelo', 'Valor', 'Vencimento', 'Contato', 'Status', 'Data/Hora Envio'];
    
    // Adicionar tabela
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });
    
    // Salvar arquivo
    doc.save(`lote_${selectedLote?.id}_${exportStatusFilter === 'all' ? 'todos' : exportStatusFilter.toLowerCase()}.pdf`);
    
    setShowExportModal(false);
    toast({
      title: "Sucesso",
      description: "Relatório PDF gerado com sucesso!",
    });
  };

  // Função para exportar em XLSX
  const exportToXLSX = () => {
    const filteredDisparos = getFilteredDisparosForExport();
    const isAniversario = selectedLote?.tipo_envio === 'ANIVERSARIO';
    
    // Preparar dados para o Excel - colunas condicionais por tipo
    const excelData = filteredDisparos.map(disparo => {
      const statusDisplay = (isAniversario && disparo.status === 'BAIXADO') ? 'Enviado' : disparo.status;
      const base: Record<string, string | number> = {
        'Nome': disparo.nome,
        [isAniversario ? 'Data de Aniversário' : 'Vencimento']: disparo.vencimento || '-',
        'Contato': disparo.contato,
        'Status': statusDisplay,
        'Data Envio': disparo.data_envio && disparo.hora_envio
          ? `${disparo.data_envio} ${disparo.hora_envio}`
          : '-',
        'Enviado': formatCarimboBR(disparo.sent_at),
        'Entregue': formatCarimboBR(disparo.delivered_at),
        'Lido': formatCarimboBR(disparo.read_at),
        'Falhou': formatCarimboBR(disparo.failed_at)
      };
      if (!isAniversario) {
        base['Placa'] = disparo.placa || '-';
        base['Modelo'] = disparo.modelo || '';
        base['Valor'] = disparo.valor ?? 0;
      }
      return base;
    });
    
    // Criar workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Ajustar largura das colunas - condicional por tipo
    const colWidths = isAniversario
      ? [{ wch: 35 }, { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 18 }]
      : [{ wch: 30 }, { wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 18 }];
    ws['!cols'] = colWidths;
    
    // Adicionar planilha ao workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Disparos');
    
    // Salvar arquivo
    XLSX.writeFile(wb, `lote_${selectedLote?.id}_${exportStatusFilter === 'all' ? 'todos' : exportStatusFilter.toLowerCase()}.xlsx`);
    
    setShowExportModal(false);
    toast({
      title: "Sucesso",
      description: "Planilha Excel gerada com sucesso!",
    });
  };

  // Função para executar a exportação
  const handleExport = () => {
    if (exportFormat === 'pdf') {
      exportToPDF();
    } else {
      exportToXLSX();
    }
  };

  const handleCancelLote = async (loteId: number) => {
    try {
      await api.post(`/disparos/lotes/${loteId}/cancelar`);
      toast({
        title: "Sucesso",
        description: "Lote cancelado com sucesso",
      });
      await refreshLotes(); // Recarrega a lista
    } catch (error) {
      console.error('Erro ao cancelar lote:', error);
      toast({
        title: "Erro",
        description: "Erro ao cancelar lote",
        variant: "destructive"
      });
    }
  };

  const handleMarcarFalhasComoPendente = async (loteId: number) => {
    try {
      setIsMarcandoFalhasComoPendente(true);
      const response = await api.post(`/disparos/lotes/${loteId}/marcar-falhas-como-pendente`);
      toast({
        title: "Sucesso",
        description: (response as any)?.message || "Falhas colocadas em pendente. Use \"Enviar Agora\" para reenviar.",
      });
      await refreshLotes();
      if (showDetailsModal && selectedLote && selectedLote.id === loteId) {
        await loadLoteDisparos(loteId);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Erro ao colocar falhas em pendente";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setIsMarcandoFalhasComoPendente(false);
    }
  };

  const handleMarcarBaixadosComoPendente = async (loteId: number) => {
    try {
      setIsMarcandoBaixadosComoPendente(true);
      const response = await api.post(`/disparos/lotes/${loteId}/marcar-baixados-como-pendente`);
      toast({
        title: "Sucesso",
        description: (response as any)?.message || "Baixados colocados em pendente. Use \"Enviar Agora\" para reenviar.",
      });
      await refreshLotes();
      if (showDetailsModal && selectedLote && selectedLote.id === loteId) {
        await loadLoteDisparos(loteId);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Erro ao colocar baixados em pendente";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setIsMarcandoBaixadosComoPendente(false);
    }
  };

  const handlePauseLote = async (loteId: number) => {
    try {
      console.log(`🔄 Tentando pausar lote ${loteId}...`);
      const response = await api.post(`/disparos/lotes/${loteId}/pausar`);
      console.log(`✅ Resposta da API ao pausar:`, response);
      
      toast({
        title: "Sucesso",
        description: "Lote pausado com sucesso",
      });
      
      // Aguardar um pouco antes de recarregar para garantir que o banco foi atualizado
        setTimeout(async () => {
          console.log(`🔄 Recarregando lotes após pausar...`);
          await refreshLotes();
        }, 500);
      
    } catch (error) {
      console.error('❌ Erro ao pausar lote:', error);
      toast({
        title: "Erro",
        description: "Erro ao pausar lote",
        variant: "destructive"
      });
    }
  };


  const handleDeleteLote = async (loteId: number) => {
    const confirmDelete = await confirm({
      title: 'Excluir Lote',
      description: 'Tem certeza que deseja excluir este lote?\n\nTodos os boletos relacionados serão removidos permanentemente.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'destructive'
    });
    if (!confirmDelete) return;

    try {
      await api.delete(`/disparos/lotes/${loteId}`);
      toast({
        title: 'Sucesso',
        description: 'Lote excluído com sucesso',
      });
      await refreshLotes();
    } catch (error) {
      console.error('Erro ao excluir lote:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir lote',
        variant: 'destructive',
      });
    }
  };

  const handleDistribuicao = (loteId: number) => {
    setSelectedLoteForDistribuicao(loteId);
    setShowDistribuicaoModal(true);
  };

  const handleConciliacao = (loteId: number) => {
    setSelectedLoteForConciliacao(loteId);
    setShowConciliacaoModal(true);
  };

  // Verificar status de distribuição para cada lote
  const checkDistribuicaoStatus = async (loteId: number) => {
    try {
      console.log(`🔍 [Distribuição] Verificando status para lote ${loteId}...`);
      const response = await api.get(`/disparos/lotes/${loteId}/distribuicao/status`) as DistribuicaoStatusResponse;
      console.log(`📊 [Distribuição] Resposta para lote ${loteId}:`, response);
      
      if (response.success) {
        console.log(`✅ [Distribuição] Lote ${loteId} - hasDistribution: ${response.hasDistribution}, total: ${response.total}`);
        setDistribuicaoStatus(prev => ({
          ...prev,
          [loteId]: {
            hasDistribution: response.hasDistribution,
            total: response.total || 0
          }
        }));
      } else {
        console.warn(`⚠️ [Distribuição] Resposta sem success para lote ${loteId}:`, response);
      }
    } catch (error: any) {
      console.error(`❌ [Distribuição] Erro ao verificar status do lote ${loteId}:`, error);
      console.error(`❌ [Distribuição] Detalhes do erro:`, error.response?.data || error.message);
    }
  };

  // Verificar distribuição para todos os lotes visíveis
  useEffect(() => {
    if (lotes.length > 0) {
      console.log(`🔄 [Distribuição] Verificando status de ${lotes.length} lotes...`);
      lotes.forEach(lote => {
        checkDistribuicaoStatus(lote.id);
      });
    }
  }, [lotes]);

  const handleDeleteDisparo = async (disparoId: number) => {
    const confirmDelete = await confirm({
      title: 'Excluir Disparo',
      description: 'Tem certeza que deseja excluir este disparo?\n\nEsta ação é irreversível.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'destructive'
    });
    if (!confirmDelete) return;

    try {
      await api.delete(`/disparos/${disparoId}`);
      toast({ title: 'Sucesso', description: 'Disparo excluído com sucesso' });
      // Recarregar disparos do lote e lista de lotes para refletir contadores
      if (selectedLote) {
        await loadLoteDisparos(selectedLote.id);
      }
      await refreshLotes();
    } catch (error) {
      console.error('Erro ao excluir disparo:', error);
      toast({ title: 'Erro', description: 'Erro ao excluir disparo', variant: 'destructive' });
    }
  };

  const handleImportLote = (loteId: number) => {
    // TODO: Implementar lógica de importação de lote específico
    toast({
      title: "Importação de Lote",
      description: `Importando lote #${loteId}. Esta funcionalidade será implementada em breve.`,
    });
  };

  const handleImportLotes = () => {
    // TODO: Implementar lógica de importação de todos os lotes
    toast({
      title: "Importação de Lotes",
      description: `Importando todos os ${lotes.length} lotes. Esta funcionalidade será implementada em breve.`,
    });
  };

  const handleEnviarAgora = async (loteId: number, enviarEmail: boolean) => {
    try {
      const response = await api.post(`/disparos/lotes/${loteId}/enviar`, { enviar_email: enviarEmail });
      
      const { validCount, invalidCount } = (response as any);
      
      toast({
        title: "Validação Concluída",
        description: `${validCount} números válidos enviados para fila. ${invalidCount} números inválidos encontrados.`,
      });
      
      await refreshLotes(); // Recarrega a lista
      
      // Se o modal de detalhes estiver aberto para este lote, recarregar os detalhes
      if (showDetailsModal && selectedLote && selectedLote.id === loteId) {
        await loadLoteDisparos(loteId);
      }
    } catch (error: any) {
      console.error('❌ [Disparos] Erro ao enviar lote:', error);
      console.log('🔍 [Disparos] Erro completo:', JSON.stringify(error, null, 2));
      console.log('🔍 [Disparos] error.response:', error.response);
      console.log('🔍 [Disparos] error.response?.data:', error.response?.data);
      
      // Log mais simples para garantir que aparece
      console.log('🚨 [Disparos] DEBUG: Erro capturado:', error.response?.data || 'Sem dados de resposta');
      
      // Verificar múltiplas estruturas possíveis de resposta de erro
      const responseData = error.response?.data || {};
      const waitSeconds = responseData.waitTimeSeconds || responseData.waitTime || responseData.wait_time_seconds;
      const nextWindowStart = responseData.nextWindowStart || responseData.next_window_start;
      const errorMessage = responseData.error || responseData.message || "";
      
      console.log('🔍 [Disparos] responseData completo:', responseData);
      
      console.log('🔍 [Disparos] waitSeconds:', waitSeconds);
      console.log('🔍 [Disparos] nextWindowStart:', nextWindowStart);
      console.log('🔍 [Disparos] errorMessage:', errorMessage);

      // 🚫 VALIDAÇÃO DE JANELA DE ENVIO DESABILITADA - Permite envio de múltiplos lotes
      // const isWindowError = waitSeconds || 
      //   errorMessage.toLowerCase().includes('janela') || 
      //   errorMessage.toLowerCase().includes('horário') ||
      //   errorMessage.toLowerCase().includes('window') ||
      //   errorMessage.toLowerCase().includes('fora') ||
      //   errorMessage.toLowerCase().includes('08:00') ||
      //   errorMessage.toLowerCase().includes('18:00');
      
      console.log('🔍 [Disparos] Validação de janela de envio desabilitada');
      
      // Sempre tratar como erro genérico (sem bloqueio de múltiplos envios)
      toast({
        title: "Erro ao processar envio",
        description: errorMessage || "Não foi possível processar o envio do lote. Verifique as configurações e tente novamente.",
        variant: "destructive"
      });
    } finally {
      // Loading state is managed by the context
    }
  };

  const handleGerarRascunhoEmail = async (loteId: number) => {
    try {
      const resp = await api.post<any>(`/disparos/lotes/${loteId}/gerar-rascunho-email`);
      if (resp.success) {
        toast({
          title: 'Rascunho de email gerado',
          description: `${resp.com_email} contato(s) com email${typeof resp.sem_email === 'number' ? `, ${resp.sem_email} sem email` : ''}.`,
        });
      } else {
        toast({
          title: 'Rascunho não gerado',
          description: resp.motivo || 'Sem detalhes',
          variant: 'destructive',
        });
      }
    } catch (e: any) {
      toast({
        title: 'Erro',
        description: e?.response?.data?.error || e?.message || 'Falha ao gerar rascunho',
        variant: 'destructive',
      });
    }
  };

  const getLoteStatusBadge = (status: string) => {
    const statusConfig = {
      'PENDENTE': { label: 'Pendente', variant: 'outline' as const, icon: Clock },
      'ENVIANDO': { label: 'Enviando', variant: 'default' as const, icon: Send, color: 'bg-blue-500 hover:bg-blue-600' },
      'CONCLUÍDO': { label: 'Concluído', variant: 'default' as const, icon: CheckCircle, color: 'bg-green-500 hover:bg-green-600' },
      'PAUSADO': { label: 'Pausado', variant: 'secondary' as const, icon: Pause, color: 'bg-orange-500 hover:bg-orange-600' },
      'CANCELADO': { label: 'Cancelado', variant: 'destructive' as const, icon: XCircle },
      'EM_ANDAMENTO': { label: 'Em Andamento', variant: 'default' as const, icon: TrendingUp, color: 'bg-blue-500 hover:bg-blue-600' }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    
    // Se o status não estiver mapeado, usar configuração padrão
    if (!config) {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <HelpCircle className="h-3 w-3" />
          {status}
        </Badge>
      );
    }
    
    const Icon = config.icon;
    const colorClass = 'color' in config && config.color ? config.color : '';
    const customClass = 'className' in config && config.className ? config.className : '';

    return (
      <Badge variant={config.variant} className={`flex items-center gap-1 ${colorClass} ${customClass}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatMotivoRejeicao = (motivo?: string) => {
    if (!motivo || motivo.trim() === '') return null;
    return motivo.replace(/_/g, ' ').trim();
  };

  const getDisparoStatusBadge = (status: string, isAniversario?: boolean, motivoRejeicao?: string) => {
    // Para aniversário: BAIXADO não faz sentido (termo financeiro) - exibir como "Enviado"
    const displayStatus = (isAniversario && status === 'BAIXADO') ? 'ENVIADO' : status;
    const statusConfig = {
      'ENVIADO': { label: 'Enviado', variant: 'default' as const, icon: CheckCircle, color: 'bg-green-500 hover:bg-green-600' },
      'PENDENTE': { label: 'Pendente', variant: 'outline' as const, icon: Clock },
      'FILA': { label: 'Em Fila', variant: 'secondary' as const, icon: Send },
      'AGUARDANDO_CONFIRMACAO': { label: 'Aguardando Confirmação', variant: 'secondary' as const, icon: Clock, className: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200' },
      'FALHA': { label: 'Falha', variant: 'destructive' as const, icon: XCircle },
      'CANCELADO': { label: 'Cancelado', variant: 'secondary' as const, icon: X },
      'CONTATO_INVALIDO': { label: 'Contato Inválido', variant: 'destructive' as const, icon: AlertTriangle },
      'BAIXADO': { label: 'Baixado', variant: 'secondary' as const, icon: Download, className: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200' },
      'EXCLUIDO': { label: 'Excluído', variant: 'secondary' as const, icon: Trash2 }
    };

    const config = statusConfig[displayStatus as keyof typeof statusConfig];
    
    // Se o status não estiver mapeado, usar configuração padrão
    if (!config) {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <HelpCircle className="h-3 w-3" />
          {status}
        </Badge>
      );
    }
    
    const Icon = config.icon;
    const colorClass = 'color' in config && config.color ? config.color : '';
    const customClass = 'className' in config && config.className ? config.className : '';
    const mostraMotivo = (status === 'FALHA' || status === 'CONTATO_INVALIDO') && motivoRejeicao;

    return (
      <div className="flex flex-col gap-0.5">
        <Badge variant={config.variant} className={`flex items-center gap-1 w-fit ${colorClass} ${customClass}`}>
          <Icon className="h-3 w-3" />
          {config.label}
        </Badge>
        {mostraMotivo && (
          <span className="text-xs text-muted-foreground max-w-[200px] line-clamp-2" title={motivoRejeicao}>
            {formatMotivoRejeicao(motivoRejeicao)}
          </span>
        )}
      </div>
    );
  };

  // Obter tags únicas dos lotes para o filtro
  const uniqueTags = Array.from(new Set(
    lotes
      .map(lote => lote.tag)
      .filter(tag => tag && typeof tag === 'string' && tag.trim() !== '')
  )).sort();
  
  // Debug: verificar se há tags inválidas
  console.log('🏷️ [Disparos] Tags únicas encontradas:', uniqueTags);
  console.log('🏷️ [Disparos] Lotes com tags:', lotes.filter(l => l.tag).map(l => ({ id: l.id, tag: l.tag })));

  const normalizedSearch = searchTerm.toLowerCase();
  const filteredLotes = lotes.filter(lote => {
    const nome = (lote.nome ?? '').toLowerCase();
    const tipoEnvio = (lote.tipo_envio ?? '').toLowerCase();
    const tag = (lote.tag ?? '').toLowerCase();
    
    // Filtro de busca por texto
    const matchesSearch = nome.includes(normalizedSearch) ||
                         tipoEnvio.includes(normalizedSearch) ||
                         tag.includes(normalizedSearch);
    
    // Filtro por status
    const matchesStatus = statusFilter === 'all' || lote.status === statusFilter;
    
    // Filtro por tag
    const matchesTag = tagFilter === 'all' || 
                      (tagFilter === 'sem_tag' && !lote.tag) ||
                      (lote.tag === tagFilter);
    
    return matchesSearch && matchesStatus && matchesTag;
  });

  // Dados para o gráfico de envios por dia (últimos 7 dias)
  const enviosPorDia = React.useMemo(() => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return d.toISOString().slice(0, 10); // YYYY-MM-DD
    });
    return last7Days.map(dateStr => {
      const envios = lotes
        .filter(l => {
          const d = (l.created_at || l.createdAt || '').slice(0, 10);
          return d === dateStr;
        })
        .reduce((acc, l) => acc + (l.total_enviados || 0), 0);
      return {
        data: dateStr,
        dataFormatada: new Date(dateStr + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
        envios
      };
    });
  }, [lotes]);

  // Dados para cards por tipo de envio
  const porTipoEnvio = React.useMemo(() => {
    const tipos: Record<string, { total: number; boletos: number }> = {};
    lotes.forEach(l => {
      const t = (l.tipo_envio || 'OUTROS').toUpperCase();
      if (t === 'BOAS_VINDAS' || t === 'BOAS-VINDAS') return;
      if (!tipos[t]) tipos[t] = { total: 0, boletos: 0 };
      tipos[t].total += l.total_enviados || 0;
      tipos[t].boletos += l.total_boletos || 0;
    });
    const labels: Record<string, string> = {
      PAGAMENTO: 'Pagamento',
      COBRANCA: 'Cobrança',
      COBRANÇA: 'Cobrança',
      REVISTORIA: 'Revistoria',
      ANIVERSARIO: 'Aniversário',
      LEMBRETE: 'Lembrete',
      REATIVACAO: 'Reativação',
      MARKETING: 'Marketing',
      RECUPERACAO: 'Recuperação',
      OUTROS: 'Outros'
    };
    return Object.entries(tipos).map(([tipo, dados]) => ({
      tipo,
      label: labels[tipo] || tipo.replace(/_/g, ' '),
      envios: dados.total,
      boletos: dados.boletos
    })).sort((a, b) => b.envios - a.envios);
  }, [lotes]);

  if (user?.role === 'admin' && (isLoadingClientes || clientes.length === 0)) {
    return (
      <Layout>
        <AtomicLoadingScreen />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Hero Header - Cores do sistema (primary) */}
        <div className="relative overflow-hidden rounded-2xl welcome-banner shadow-xl">
          <div className="relative px-6 py-8 md:px-8 md:py-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white/95">
                  Disparos de Boletos
                </h1>
                <p className="mt-1 text-sm text-white/90">
                  Gerencie os lotes de disparos de boletos
                </p>
                {user?.role === 'admin' && (
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <Label className="text-white/90">Cliente:</Label>
                    <Button
                      variant="outline"
                      onClick={() => setShowClientModal(true)}
                      className="justify-between gap-2 h-11 min-w-[280px] font-medium bg-white/15 border-white/25 text-white hover:bg-white/25 hover:border-white/40"
                    >
                      <span className="truncate">{clienteNome || 'Selecione o cliente'}</span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-70" />
                    </Button>
                    <ClientSelectModal
                      open={showClientModal}
                      onOpenChange={setShowClientModal}
                      clientes={clientes.filter(c => c.id && c.id > 0)}
                      value={clienteId}
                      onSelect={id => setClienteId(id)}
                    />
                  </div>
                )}
                {user?.role === 'admin' && !clienteId && (
                  <p className="mt-4 text-sm text-white/60">Selecione um cliente para visualizar os lotes.</p>
                )}
              </div>

              {/* Central da Comunicação - CTA principal em destaque */}
              {user?.role === 'admin' && clienteId && (
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {loadingIntegration ? (
                    <div className="flex items-center gap-2 text-sm text-white/80">
                      <div className="animate-spin h-4 w-4 border-2 border-white/60 border-t-transparent rounded-full" />
                      <span>Verificando integração...</span>
                    </div>
                  ) : hasImportIntegration ? (
                    <>
                      <span className="text-xs text-white/60 font-medium uppercase tracking-wider">
                        Hub de Comunicação
                      </span>
                      <ImportBoletosModal
                        clientId={clienteId}
                        onSuccess={refreshLotes}
                        prominent
                        inHero
                      />
                      <span className="text-xs text-white/50">
                        {clientSystemType === 'south' ? 'South' : clientSystemType === 'south-v0' ? 'South V0' : clientSystemType === 'hinova' ? 'Hinova SGA' : clientSystemType || 'Sistema Integrado'}
                      </span>
                    </>
                  ) : clientSystemType ? (
                    <div className="flex flex-col items-end gap-1 text-xs text-white/50">
                      <span className="px-2 py-1 rounded-md bg-white/10">
                        {clientSystemType === 'south' ? 'South' : clientSystemType === 'south-v0' ? 'South V0' : clientSystemType === 'hinova' ? 'Hinova SGA' : clientSystemType}
                      </span>
                      <span>Integração não configurada</span>
                    </div>
                  ) : (
                    <ImportBoletosModal
                      clientId={clienteId}
                      onSuccess={refreshLotes}
                      prominent
                      inHero
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {isLoadingLotes && clienteId ? (
          <AtomicLoadingScreen />
        ) : user?.role === 'admin' && !clienteId ? null : (
          <>
            {/* Botão Atualizar Dados - Em evidência */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <Button
                onClick={refreshLotes}
                variant="default"
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Atualizar Dados Manualmente
              </Button>
            </div>

            {/* Resumo Executivo - Painel impactante */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
              <div className="p-6 md:p-8">
                <div className="mb-6">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
                    Resumo Executivo
                  </h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Visão geral dos disparos em tempo real
                  </p>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="group flex items-start gap-4 rounded-xl bg-muted/50 p-5 transition-colors hover:bg-muted/80">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <FileText className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Total de Lotes</p>
                      <p className="mt-1 text-2xl font-bold tracking-tight text-foreground tabular-nums">{lotes.length}</p>
                    </div>
                  </div>
                  <div className="group flex items-start gap-4 rounded-xl bg-muted/50 p-5 transition-colors hover:bg-muted/80">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <DollarSign className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Total de Boletos</p>
                      <p className="mt-1 text-2xl font-bold tracking-tight text-foreground tabular-nums">
                        {lotes.reduce((acc, lote) => acc + lote.total_boletos, 0).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <div className="group flex items-start gap-4 rounded-xl bg-primary/5 p-5 ring-1 ring-primary/10 transition-all hover:bg-primary/10 hover:ring-primary/20">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                      <TrendingUp className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Valor Total</p>
                      <p className="mt-1 text-2xl font-bold tracking-tight text-foreground tabular-nums">
                        R$ {lotes.reduce((acc, lote) => acc + lote.valor_total, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  <div className="group flex items-start gap-4 rounded-xl bg-muted/50 p-5 transition-colors hover:bg-muted/80">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <BarChart className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Taxa de Envio</p>
                      <p className="mt-1 text-2xl font-bold tracking-tight text-foreground tabular-nums">
                        {lotes.length > 0 
                          ? `${Math.round(
                              (lotes.reduce((acc, l) => acc + l.total_enviados, 0) / 
                               Math.max(lotes.reduce((acc, l) => acc + l.total_boletos, 0), 1)) * 100
                            )}%`
                          : '0%'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Gráfico compacto + Resumo por tipo de envio - lado a lado */}
            <div className="grid gap-4 lg:grid-cols-5">
              <Card className="lg:col-span-2 border-0 shadow-md rounded-xl overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Envios por dia</CardTitle>
                  <CardDescription className="text-xs">
                    Últimos 7 dias
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <ChartContainer
                    config={{
                      envios: { label: 'Envios', color: '#D97757' },
                      dataFormatada: { label: 'Data', color: '#737373' }
                    }}
                    className="h-[180px] aspect-auto [&_.recharts-wrapper]:!w-full"
                  >
                    <RechartsBarChart data={enviosPorDia} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                      <XAxis dataKey="dataFormatada" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={32} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="envios" fill="#D97757" radius={[4, 4, 0, 0]} name="Envios" barSize={24} />
                    </RechartsBarChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              <Card className="lg:col-span-3 border-0 shadow-md rounded-xl overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Resumo por tipo de envio</CardTitle>
                  <CardDescription className="text-xs">
                    Distribuição dos envios
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid gap-2 sm:grid-cols-2">
                    {porTipoEnvio.map((item) => {
                      const iconMap: Record<string, React.ReactNode> = {
                        PAGAMENTO: <CreditCard className="h-4 w-4" />,
                        COBRANCA: <DollarSign className="h-4 w-4" />,
                        COBRANÇA: <DollarSign className="h-4 w-4" />,
                        REVISTORIA: <FileText className="h-4 w-4" />,
                        ANIVERSARIO: <Gift className="h-4 w-4" />,
                        LEMBRETE: <Clock className="h-4 w-4" />,
                        REATIVACAO: <TrendingUp className="h-4 w-4" />,
                        MARKETING: <BarChart className="h-4 w-4" />,
                        RECUPERACAO: <RefreshCw className="h-4 w-4" />,
                      };
                      const Icon = iconMap[item.tipo] || <FileText className="h-4 w-4" />;
                      return (
                        <div
                          key={item.tipo}
                          className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2"
                        >
                          <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            {Icon}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-foreground">{item.label}</p>
                            <p className="text-sm font-bold text-foreground tabular-nums">
                              {item.envios.toLocaleString('pt-BR')} envios
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {porTipoEnvio.length === 0 && (
                      <p className="col-span-full text-sm text-muted-foreground py-3">
                        Nenhum dado de envio no período.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Tabs value={disparoTab} onValueChange={(v) => setDisparoTab(v as 'whatsapp' | 'email')}>
              <TabsList>
                <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
                <TabsTrigger value="email">Email</TabsTrigger>
              </TabsList>

              <TabsContent value="whatsapp">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar lotes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="CONCLUÍDO">Concluído</SelectItem>
                  <SelectItem value="REGISTRANDO_LOTE">Registrando Lote</SelectItem>
                  <SelectItem value="CONFERINDO_CONTATOS">Conferindo Contatos</SelectItem>
                  <SelectItem value="ENVIANDO">Enviando</SelectItem>
                  <SelectItem value="PAUSADO">Pausado</SelectItem>
                  <SelectItem value="PENDENTE">Pendente</SelectItem>
                  <SelectItem value="EM_ANDAMENTO">Em Andamento</SelectItem>
                  <SelectItem value="CANCELADO">Cancelado</SelectItem>
                </SelectContent>
              </Select>

              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Tags</SelectItem>
                  <SelectItem value="sem_tag">Sem Tag</SelectItem>
                  {uniqueTags.filter(tag => tag && tag.trim() !== '').map(tag => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Seletor de Conexão para Admin */}
              {user?.role === 'admin' && clienteId && (
                loadingAdminConnections ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                    <span>Carregando conexões...</span>
                  </div>
                ) : adminConnections.length > 1 ? (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Conexão:</span>
                    <Select 
                      value={selectedAdminConnectionId?.toString() || ''} 
                      onValueChange={(value) => setSelectedAdminConnectionId(parseInt(value))}
                    >
                      <SelectTrigger className="w-[250px]">
                        <SelectValue placeholder="Selecione a conexão" />
                      </SelectTrigger>
                      <SelectContent>
                        {adminConnections.map((connection) => (
                          <SelectItem key={connection.id} value={connection.id.toString()}>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{connection.name}</span>
                              <Badge 
                                variant="outline" 
                                className={connection.is_online ? "text-green-600 border-green-600" : "text-gray-500 border-gray-400"}
                              >
                                {connection.is_online ? 'Online' : 'Offline'}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : adminConnections.length === 1 ? (
                  <div className="text-sm text-gray-600">
                    <span>Conexão: <strong>{adminConnections[0].name}</strong></span>
                  </div>
                ) : null
              )}
            </div>

            {/* Visualização do Progresso Diário */}
            <DailyProgressVisualization 
              clientId={user?.role === 'admin' ? clienteId : user?.client_id}
              selectedConnectionId={user?.role === 'admin' ? selectedAdminConnectionId : null}
            />

            {/* Lotes Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Lotes de Disparos</CardTitle>
                    <CardDescription>
                      Visualize e gerencie seus lotes de disparos de boletos (últimos 7 dias)
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowHistoricoModal(true)}
                      className="flex items-center gap-2"
                    >
                      <Calendar className="h-4 w-4" />
                      Histórico de Lotes
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome do Lote</TableHead>
                      <TableHead>Tipo de Envio</TableHead>
                      <TableHead>Tag</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Total Boletos</TableHead>
                      <TableHead className="text-center">Enviados</TableHead>
                      <TableHead className="text-center">Aguard. Conf.</TableHead>
                      <TableHead className="text-center">Em Fila</TableHead>
                      <TableHead className="text-center">Pendentes</TableHead>
                      <TableHead className="text-center">Inválidos</TableHead>
                      <TableHead className="text-center">Bloqueados</TableHead>
                      <TableHead className="text-center">Falhas</TableHead>
                      <TableHead className="text-center">Baixados</TableHead>
                      <TableHead className="text-right">Valor Total</TableHead>
                      <TableHead>Criado em</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLotes.map((lote) => (
                      <TableRow key={lote.id}>
                        <TableCell className="font-medium">{lote.nome || `Lote #${lote.id}`}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {lote.tipo_envio}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {lote.tag ? (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              {lote.tag}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>{getLoteStatusBadge(lote.status)}</TableCell>
                        <TableCell className="text-center">{lote.total_boletos}</TableCell>
                        <TableCell className="text-center">{lote.total_enviados}</TableCell>
                        <TableCell className="text-center">{lote.total_aguardando_confirmacao ?? 0}</TableCell>
                        <TableCell className="text-center">{lote.total_fila || 0}</TableCell>
                        <TableCell className="text-center">{lote.total_pendentes}</TableCell>
                        <TableCell className="text-center">{lote.total_invalidos || 0}</TableCell>
                        <TableCell className="text-center">{lote.total_bloqueados || 0}</TableCell>
                        <TableCell className="text-center">{lote.total_falhas}</TableCell>
                        <TableCell className="text-center">{lote.total_baixados || 0}</TableCell>
                        <TableCell className="text-right">
                          R$ {(lote.valor_total ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          {formatDateBR(lote.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center space-x-2">
                            <Button variant="outline" size="sm" onClick={() => handleViewDetails(lote)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Ver Detalhes
                            </Button>
                            
                            {/* Botões de Controle de Status */}
                            {(() => {
                              console.log(`🔍 Renderizando botões para lote ${lote.id}: status = "${lote.status}"`);
                              return null;
                            })()}
                            
                            {lote.status === 'ENVIANDO' && (
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => handlePauseLote(lote.id)}
                              >
                                <Pause className="mr-2 h-4 w-4" />
                                Pausar
                              </Button>
                            )}
                            {(lote.total_falhas ?? lote.quantidade_falhas ?? 0) > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarcarFalhasComoPendente(lote.id)}
                                disabled={isMarcandoFalhasComoPendente}
                                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                              >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Reenviar falhas
                              </Button>
                            )}
                            {(lote.total_baixados ?? lote.quantidade_baixados ?? 0) > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarcarBaixadosComoPendente(lote.id)}
                                disabled={isMarcandoBaixadosComoPendente}
                                className="border-purple-500 text-purple-700 hover:bg-purple-50"
                              >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Reenviar baixados
                              </Button>
                            )}
                            {/* Botão Distribuição */}
                            {distribuicaoStatus[lote.id]?.hasDistribution ? (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDistribuicao(lote.id)}
                                className="border-green-500 text-green-700 hover:bg-green-50"
                              >
                                <UserCheck className="mr-2 h-4 w-4" />
                                Já distribuído ({distribuicaoStatus[lote.id].total})
                              </Button>
                            ) : (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDistribuicao(lote.id)}
                                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                              >
                                <UserCheck className="mr-2 h-4 w-4" />
                                Distribuição
                              </Button>
                            )}
                            
                            {/* Botao Conciliar */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConciliacao(lote.id)}
                              className="border-orange-500 text-orange-700 hover:bg-orange-50"
                            >
                              <FileSearch className="mr-2 h-4 w-4" />
                              Conciliar
                            </Button>

                            {/* Botão Gerar rascunho de email */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGerarRascunhoEmail(lote.id)}
                              className="border-sky-500 text-sky-700 hover:bg-sky-50"
                            >
                              <Mail className="mr-2 h-4 w-4" />
                              Gerar rascunho de email
                            </Button>

                            {/* Botão Excluir */}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteLote(lote.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Excluir
                            </Button>
                            
                            {/* Botão Enviar Agora - não mostrar para lotes concluídos */}
                            {lote.status !== 'CONCLUÍDO' && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => { setEnviarEmailJunto(clienteEmailCanalEnabled); setEnviarDialog({ open: true, loteId: lote.id, emailEnabled: clienteEmailCanalEnabled }); }}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Send className="mr-2 h-4 w-4" />
                                Enviar Agora
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
              </TabsContent>

              <TabsContent value="email">
                <EmailLotesPanel clientId={clienteId ?? null} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Lote Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Lote</DialogTitle>
            <DialogDescription>
              Lote {selectedLote?.id} - Visualize todos os disparos do lote
            </DialogDescription>
          </DialogHeader>

          {/* Botões Exportar, Colocar falhas em pendente e Colocar baixados em pendente */}
          <div className="flex justify-end gap-2 mb-4">
            {selectedLote && (selectedLote.total_falhas ?? selectedLote.quantidade_falhas ?? 0) > 0 && (
              <Button
                variant="outline"
                onClick={() => handleMarcarFalhasComoPendente(selectedLote.id)}
                disabled={isMarcandoFalhasComoPendente}
                className="flex items-center gap-2 border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                {isMarcandoFalhasComoPendente ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {isMarcandoFalhasComoPendente ? 'Colocando...' : 'Colocar falhas em pendente'}
              </Button>
            )}
            {selectedLote && (selectedLote.total_baixados ?? selectedLote.quantidade_baixados ?? 0) > 0 && (
              <Button
                variant="outline"
                onClick={() => handleMarcarBaixadosComoPendente(selectedLote.id)}
                disabled={isMarcandoBaixadosComoPendente}
                className="flex items-center gap-2 border-purple-500 text-purple-700 hover:bg-purple-50"
              >
                {isMarcandoBaixadosComoPendente ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {isMarcandoBaixadosComoPendente ? 'Colocando...' : 'Colocar baixados em pendente'}
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Exportar Lote
            </Button>
          </div>

          {selectedLote && (
            <div className="space-y-6">
              {(() => {
                const isAniversario = selectedLote.tipo_envio === 'ANIVERSARIO';
                return (
              <>
              {/* Lote Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Informações do Lote</h3>
                  <div className="mt-2 space-y-1">
                    <p><span className="font-medium">ID:</span> {selectedLote.id}</p>
                    <p><span className="font-medium">Tipo de Envio:</span> {selectedLote.tipo_envio}</p>
                    <div className="flex items-center gap-2"><span className="font-medium">Status:</span> {getLoteStatusBadge(selectedLote.status)}</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Estatísticas</h3>
                  <div className="mt-2 space-y-1">
                    <p><span className="font-medium">{isAniversario ? 'Total de Aniversariantes' : 'Total de Boletos'}:</span> {selectedLote.quantidade_total}</p>
                    <p><span className="font-medium">Enviados:</span> {selectedLote.quantidade_enviados}</p>
                    <p><span className="font-medium">Pendentes:</span> {selectedLote.quantidade_pendentes}</p>
                    <p><span className="font-medium">Inválidos:</span> {selectedLote.quantidade_invalidos || 0}</p>
                    <p><span className="font-medium">Falhas:</span> {selectedLote.quantidade_falhas}</p>
                    {!isAniversario && (
                      <p><span className="font-medium">Baixados:</span> {selectedLote.quantidade_baixados}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Search and Filter Disparos */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar disparos..."
                    value={disparoSearchTerm}
                    onChange={(e) => setDisparoSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="status-filter" className="text-sm font-medium whitespace-nowrap">
                    Status:
                  </Label>
                  <Select value={disparoStatusFilter} onValueChange={setDisparoStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filtrar por status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Badge variant="outline" className="px-3 py-1">
                  {loteDisparos.filter(disparo => {
                    const matchesSearch = disparo.nome.toLowerCase().includes(disparoSearchTerm.toLowerCase()) ||
                      disparo.placa.toLowerCase().includes(disparoSearchTerm.toLowerCase()) ||
                      disparo.contato.includes(disparoSearchTerm) ||
                      (disparo.vencimento && disparo.vencimento.includes(disparoSearchTerm));
                    
                    const matchesStatus = disparoStatusFilter === 'all' || disparo.status === disparoStatusFilter;
                    
                    return matchesSearch && matchesStatus;
                  }).length} de {loteDisparos.length} disparos
                </Badge>
              </div>

              {/* Disparos Table - Colunas condicionais por tipo de envio */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      {!isAniversario && <TableHead>Placa</TableHead>}
                      {!isAniversario && <TableHead>Modelo</TableHead>}
                      {!isAniversario && <TableHead>Valor</TableHead>}
                      <TableHead>{isAniversario ? 'Data de Aniversário' : 'Vencimento'}</TableHead>
                      <TableHead>Contato</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Entrega</TableHead>
                      <TableHead>Enviado</TableHead>
                      <TableHead>Entregue</TableHead>
                      <TableHead>Lido</TableHead>
                      <TableHead>Falhou</TableHead>
                      <TableHead>Data/Hora Envio</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {disparosLoading ? (
                      <TableRow>
                        <TableCell colSpan={isAniversario ? 11 : 14} className="text-center py-8">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="ml-2">Carregando disparos...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      loteDisparos
                        .filter(disparo => {
                          const matchesSearch = disparo.nome.toLowerCase().includes(disparoSearchTerm.toLowerCase()) ||
                            (disparo.placa && disparo.placa.toLowerCase().includes(disparoSearchTerm.toLowerCase())) ||
                            disparo.contato.includes(disparoSearchTerm) ||
                            (disparo.vencimento && disparo.vencimento.includes(disparoSearchTerm));
                          
                          const matchesStatus = disparoStatusFilter === 'all' || disparo.status === disparoStatusFilter;
                          
                          return matchesSearch && matchesStatus;
                        })
                        .map((disparo) => (
                          <TableRow key={disparo.id}>
                            <TableCell className="font-medium">{disparo.nome}</TableCell>
                            {!isAniversario && (
                              <TableCell>{disparo.placa && disparo.placa.trim() ? disparo.placa : '-'}</TableCell>
                            )}
                            {!isAniversario && <TableCell>{disparo.modelo}</TableCell>}
                            {!isAniversario && (
                              <TableCell>
                                R$ {(disparo.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </TableCell>
                            )}
                            <TableCell>
                              {disparo.vencimento || '-'}
                            </TableCell>
                            <TableCell>{disparo.contato}</TableCell>
                            <TableCell>
                              {getDisparoStatusBadge(disparo.status, isAniversario, disparo.motivo_rejeicao)}
                            </TableCell>
                            <TableCell>
                              {disparo.delivery_status ? (
                                <div className="flex flex-col gap-0.5">
                                  <Badge
                                    className={(DELIVERY_BADGE[disparo.delivery_status] || DELIVERY_BADGE.sent).className}
                                    title={disparo.delivery_status === 'failed' ? deliveryFailTooltip(disparo) : ''}
                                  >
                                    {(DELIVERY_BADGE[disparo.delivery_status] || { label: disparo.delivery_status }).label}
                                  </Badge>
                                  {disparo.delivery_status === 'failed' && (disparo.delivery_error_pt || disparo.delivery_error_title) && (
                                    <span className="text-[11px] leading-tight text-red-600">
                                      {disparo.delivery_error_pt || disparo.delivery_error_title}
                                      {disparo.delivery_error_categoria_label ? ` · ${disparo.delivery_error_categoria_label}` : ''}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">{formatCarimboBR(disparo.sent_at)}</TableCell>
                            <TableCell className="text-sm text-gray-600">{formatCarimboBR(disparo.delivered_at)}</TableCell>
                            <TableCell className="text-sm text-gray-600">{formatCarimboBR(disparo.read_at)}</TableCell>
                            <TableCell className="text-sm text-gray-600">{formatCarimboBR(disparo.failed_at)}</TableCell>
                            <TableCell>
                              {disparo.data_envio && disparo.hora_envio
                                ? `${disparo.data_envio} ${disparo.hora_envio}`
                                : '-'
                              }
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="destructive" 
                                size="icon" 
                                onClick={() => handleDeleteDisparo(disparo.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </div>
              </>
            );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar Lote</DialogTitle>
            <DialogDescription>
              Selecione o formato e filtros para exportação do lote {selectedLote?.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Seleção de Formato */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Formato de Exportação</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                  onClick={() => setExportFormat('pdf')}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  PDF
                </Button>
                <Button
                  variant={exportFormat === 'xlsx' ? 'default' : 'outline'}
                  onClick={() => setExportFormat('xlsx')}
                  className="flex items-center gap-2"
                >
                  <Database className="h-4 w-4" />
                  Excel
                </Button>
              </div>
            </div>

            {/* Filtro por Status */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Filtrar por Status</Label>
              <Select value={exportStatusFilter} onValueChange={setExportStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Informações da Exportação */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">
                <p><strong>Total de disparos:</strong> {loteDisparos.length}</p>
                <p><strong>Serão exportados:</strong> {getFilteredDisparosForExport().length}</p>
                <p><strong>Formato:</strong> {exportFormat.toUpperCase()}</p>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowExportModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleExport} className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Exportar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Distribuição */}
      <ImportDistribuicaoModal
        isOpen={showDistribuicaoModal}
        onClose={() => {
          setShowDistribuicaoModal(false);
          setSelectedLoteForDistribuicao(null);
        }}
        loteId={selectedLoteForDistribuicao || 0}
        onSuccess={() => {
          refreshLotes();
          if (selectedLoteForDistribuicao) {
            checkDistribuicaoStatus(selectedLoteForDistribuicao);
          }
          setShowDistribuicaoModal(false);
          setSelectedLoteForDistribuicao(null);
        }}
      />

      {/* Modal de Conciliacao */}
      <ConciliarBoletosModal
        isOpen={showConciliacaoModal}
        onClose={() => {
          setShowConciliacaoModal(false);
          setSelectedLoteForConciliacao(null);
        }}
        loteId={selectedLoteForConciliacao || 0}
        onSuccess={() => {
          refreshLotes();
          setShowConciliacaoModal(false);
          setSelectedLoteForConciliacao(null);
        }}
      />

      {/* Modal de Histórico de Lotes */}
      <HistoricoLotesModal
        isOpen={showHistoricoModal}
        onClose={() => setShowHistoricoModal(false)}
        onViewDetails={(lote) => {
          setSelectedLote(lote);
          setDisparoSearchTerm('');
          setDisparoStatusFilter('all');
          setShowDetailsModal(true);
          setShowHistoricoModal(false);
          loadLoteDisparos(lote.id);
        }}
        user={user}
        clienteId={clienteId}
      />
      
      {/* Dialog de Confirmação */}
      <ConfirmationDialog />

      <AlertDialog open={enviarDialog.open} onOpenChange={(open) => setEnviarDialog((s) => ({ ...s, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar lote agora?</AlertDialogTitle>
            <AlertDialogDescription>
              Os boletos serão enfileirados para envio no WhatsApp.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {enviarDialog.emailEnabled && (
            <div className="flex items-center gap-2 py-2">
              <Checkbox
                id="enviar-email-junto"
                checked={enviarEmailJunto}
                onCheckedChange={(v) => setEnviarEmailJunto(v === true)}
              />
              <label htmlFor="enviar-email-junto" className="text-sm">
                Enviar também por email
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = enviarDialog.loteId;
                setEnviarDialog({ open: false, loteId: null, emailEnabled: false });
                if (id != null) handleEnviarAgora(id, enviarDialog.emailEnabled ? enviarEmailJunto : false);
              }}
            >
              Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </Layout>
  );
};

const Disparos = () => {
  const { user, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <Layout>
        <AtomicLoadingScreen />
      </Layout>
    );
  }

  return (
    <RoleBasedNavigation>
      <DisparosProvider user={user} clienteId={null} statusFilter="all">
        <DisparosContent />
      </DisparosProvider>
    </RoleBasedNavigation>
  );
};

export default Disparos; 